import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'bootstrap';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAddComment } from '../../services/hooks/loanCommentHooks';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

const commentSchema = z.object({
  comment: z.string().min(1, 'Comment is required').max(2000, 'Comment must be less than 2000 characters'),
});

type CommentFormData = z.infer<typeof commentSchema>;

interface CommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  loanApplicationId?: string;
  onCommentAdded?: (comment: string) => void;
}

const CommentModal: React.FC<CommentModalProps> = ({
  isOpen,
  onClose,
  loanApplicationId,
  onCommentAdded
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const modalInstanceRef = useRef<Modal | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const user = useSelector((state: RootState) => state.auth.user);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CommentFormData>({
    resolver: zodResolver(commentSchema),
  });

  const addCommentMutation = useAddComment({
    onSuccess: () => {
      toast.success('Comment added successfully');
      reset();
      onClose();
      // No need to call onCommentAdded for existing applications
    },
    onError: (error: any) => {
      toast.error(`Failed to add comment: ${error.message || 'Unknown error'}`);
    },
  });

  useEffect(() => {
    if (modalRef.current) {
      modalInstanceRef.current = new Modal(modalRef.current, {
        backdrop: 'static',
        keyboard: false,
      });
    }

    return () => {
      if (modalInstanceRef.current) {
        modalInstanceRef.current.dispose();
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen && modalInstanceRef.current) {
      modalInstanceRef.current.show();
    } else if (!isOpen && modalInstanceRef.current) {
      modalInstanceRef.current.hide();
    }
  }, [isOpen]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: CommentFormData) => {
    if (loanApplicationId) {
      // Existing application - make API call
      setIsSubmitting(true);
      try {
        await addCommentMutation.mutateAsync({
          loanApplicationId,
          comment: data.comment,
          createdBy: user?.name || user?.username || undefined,
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // New application - collect comment for later
      onCommentAdded?.(data.comment);
      reset();
      onClose();
    }
  };

  return (
    <div
      ref={modalRef}
      className="modal fade"
      tabIndex={-1}
      aria-labelledby="commentModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="commentModalLabel">
              Add Comment
            </h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={handleClose}
            ></button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-body">
              <div className="mb-3">
                <label htmlFor="comment" className="form-label">
                  Comment <span className="text-danger">*</span>
                </label>
                <textarea
                  className={`form-control ${errors.comment ? 'is-invalid' : ''}`}
                  id="comment"
                  rows={4}
                  placeholder="Enter your comment here..."
                  {...register('comment')}
                  disabled={isSubmitting}
                />
                {errors.comment && (
                  <div className="invalid-feedback">
                    {errors.comment.message}
                  </div>
                )}
                <div className="form-text">
                  Maximum 2000 characters
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Adding...
                  </>
                ) : (
                  'Add Comment'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CommentModal;